import { Box, IconButton, InputAdornment, TextField } from '@mui/material'
import { ReactElement } from 'react'
import { AttributeType } from '../../../constants/constants'
import ColumnEntityTypeFilter from './ColumnEntityTypeFilter'
import { useAppSelector } from '../../../redux/hook'
import { OrganizationColumnFilter } from './OrganizationColumnFilter'
import DateRangeInput from '../../Common/DateRangeInput'
import dayjs from 'dayjs'
import { ClearIcon } from '@mui/x-date-pickers'
import { useTranslation } from 'react-i18next'
import DateTimeRangeInput from '../../Common/DateTimeRangeInput'
import { TypeColumnFilter } from './TypeColumnFilter'
import { ProgramColumnFilter } from './ProgramColumnFilter'

export interface ColumnFilterInputProps {
    id: string
    header: string
    dataType: string
    range: string
    attributeName: string
    value: any
    onChange: (value: any) => void
    templateId: string
    fetchAllOrgsForOrgFilter?: boolean
}

/**
 * Pure filter input — no MRT dependency.
 * Used by CustomFilters (inside the table's popover) and by
 * MapFullscreenFilterOverlay (rendered inline in the overlay panel).
 */
const ColumnFilterInput = ({
    id,
    header,
    dataType,
    range,
    attributeName,
    value,
    onChange,
    templateId,
    fetchAllOrgsForOrgFilter = false,
}: ColumnFilterInputProps): ReactElement => {
    const org = useAppSelector((state) => state.org.org)
    const { t } = useTranslation()

    if (!dataType) {
        return <></>
    }

    if (dataType.toLowerCase() === AttributeType.ENTITY) {
        return (
            <Box>
                <ColumnEntityTypeFilter
                    id={id}
                    label=""
                    value={value || []}
                    onChange={(_id: string, val: any[]) => onChange(val)}
                    templateId={range}
                    orgId={org?.id || ''}
                />
            </Box>
        )
    }

    if (attributeName === 'Organization Name') {
        return (
            <OrganizationColumnFilter
                id={id}
                label={t('organization')}
                value={value || []}
                onChange={(_id: string, val: any[]) => onChange(val)}
                orgId={org?.id || ''}
                templateId={templateId}
                fetchAllOrgsForOrgFilter={fetchAllOrgsForOrgFilter}
            />
        )
    }

    if (attributeName === 'Entity Template Name') {
        return (
            <TypeColumnFilter
                id={id}
                label=""
                value={(value as string[]) || []}
                onChange={(_id: string, val: any[]) => onChange(val)}
                orgId={org?.id || ''}
                templateId={templateId}
            />
        )
    }

    if (attributeName === 'program') {
        return (
            <ProgramColumnFilter
                id={id}
                label={t('program')}
                value={value || []}
                onChange={(_id: string, val: any[]) => onChange(val)}
                orgId={org?.id || ''}
                templateId={templateId}
            />
        )
    }

    if (
        dataType.toLowerCase() === AttributeType.DATE ||
        dataType.toLowerCase() === AttributeType.CREATION_DATETIME ||
        dataType.toLowerCase() === AttributeType.SENSOR_SNAPSHOT_READINGS
    ) {
        return (
            <Box sx={{ width: '100%' }}>
                <DateRangeInput
                    id={`date-time-range-${header}`}
                    label=""
                    value={(value as any[]) || []}
                    onChange={(_id: string, val: any) => onChange(val)}
                    clearable={true}
                    minDate={dayjs().subtract(180, 'day')}
                    maxDate={dayjs()}
                />
            </Box>
        )
    }

    if (dataType.toLowerCase() === AttributeType.DATE_TIME) {
        return (
            <Box sx={{ width: '100%' }}>
                <DateTimeRangeInput
                    id={`date-time-range-${header}`}
                    label=""
                    value={(value as any[]) || []}
                    onChange={(_id: string, val: any) => onChange(val)}
                    clearable={true}
                    minDate={dayjs().subtract(180, 'day')}
                    maxDate={dayjs()}
                />
            </Box>
        )
    }

    return (
        <TextField
            size="small"
            sx={{ width: '100%' }}
            placeholder={`Search ${header}`}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            variant="outlined"
            slotProps={{
                input: {
                    endAdornment: (
                        <InputAdornment position="end">
                            {value ? (
                                <IconButton
                                    size="small"
                                    onClick={() => onChange(undefined)}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            ) : null}
                        </InputAdornment>
                    ),
                },
            }}
        />
    )
}

export default ColumnFilterInput
